import { useState, useEffect, useRef, Suspense, lazy, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SearchEngineFacade } from '@/components/SearchEngineFacade';
const SearchEngine = lazy(() => import('@/components/SearchEngine').then(m => ({ default: m.SearchEngine })));
import { LazyLoadSection } from '@/components/LazyLoadSection';
import Header from '@/components/Header';
import { SearchFilters } from '@/types/caravan';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSEO } from '@/hooks/useSEO';
import { SchemaJSONLD } from '@/components/SchemaJSONLD';

const SEARCH_CACHE_TTL = 5 * 60 * 1000;
const HOME_HERO_TITLES: Record<string, string> = {
  en: 'Campervan & Motorhome Turkey\nOnly 20% Pay to Book',
  tr: 'Karavan Kiralama Türkiye\nSadece %20 Ön Ödeme',
  de: 'Wohnmobil & Camper Türkei\nNur 20% Anzahlung',
  es: 'Alquiler Autocaravanas Turquía\nSolo 20% de Reserva',
  it: 'Noleggio Camper & Motorhome Turchia\nSolo 20% Anticipo',
  nl: 'Camper & Motorhome huren Turkije\nSlechts 20% Aanbetaling',
  ru: 'Аренда автодомов в Турции\nПредоплата всего 20%'
};
const SpecialOffersSection = lazy(() => import('@/components/SpecialOffersSection').then(m => ({ default: m.SpecialOffersSection })));
const CaravanListingSection = lazy(() => import('@/components/CaravanListingSection').then(m => ({ default: m.CaravanListingSection })));
const PopularLocationsSection = lazy(() => import('@/components/PopularLocationsSection').then(m => ({ default: m.PopularLocationsSection })));
const BlogSection = lazy(() => import('@/components/BlogSection').then(m => ({ default: m.BlogSection })));
const OtherServicesSection = lazy(() => import('@/components/OtherServicesSection').then(m => ({ default: m.OtherServicesSection })));
const PartnerProgramSection = lazy(() => import('@/components/PartnerProgramSection').then(m => ({ default: m.PartnerProgramSection })));
const WideSelectionSection = lazy(() => import('@/components/WideSelectionSection').then(m => ({ default: m.WideSelectionSection })));
const ChooseExploreSection = lazy(() => import('@/components/ChooseExploreSection').then(m => ({ default: m.ChooseExploreSection })));
const GetToKnowUsSection = lazy(() => import('@/components/GetToKnowUsSection').then(m => ({ default: m.GetToKnowUsSection })));
const FAQSection = lazy(() => import('@/components/FAQSection').then(m => ({ default: m.FAQSection })));
const Footer = lazy(() => import('@/components/Footer'));

const Index = ({ defaultCity }: { defaultCity?: string } = {}) => {
  useSEO('/');
  const navigate = useNavigate();
  const location = useLocation();
  const { t, currentLanguage } = useLanguage();
  
  const [searchActive, setSearchActive] = useState(false);
  const [searchData, setSearchData] = useState<SearchFilters | null>(null);
  const [isSearchEngineLoaded, setIsSearchEngineLoaded] = useState(false);
  const resultsRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (searchData || searchActive) {
      setIsSearchEngineLoaded(true);
    }
    
    // Fallback if interaction doesn't happen, but we prefer interaction-based loading in App.tsx
    // Keeping a safety timer just in case, but longer
    const timer = setTimeout(() => {
      setIsSearchEngineLoaded(true);
    }, 4000);
    
    return () => clearTimeout(timer);
  }, [searchData, searchActive]);

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "AutoRental",
    "name": "Campervan Rental Turkey",
    "url": "https://campervanrentalturkey.com",
    "logo": "https://campervanrentalturkey.com/logo.svg",
    "image": "https://campervanrentalturkey.com/hero-bg-optimized.webp",
    "description": "Rent a Campervan in Turkey starting with only 20% deposit. Premium Motorhomes available in Istanbul, Antalya & more.",
    "telephone": "+90 850 302 26 63",
    "email": "info@campervanrentalturkey.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Armutalan Mah. Adnan Menderes Cad. No: 22 İç Kapı No: 2",
      "addressLocality": "Marmaris",
      "addressRegion": "Muğla",
      "postalCode": "48700",
      "addressCountry": "TR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 36.8522,
      "longitude": 28.2741
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        "opens": "00:00",
        "closes": "23:59"
      }
    ],
    "priceRange": "$$",
    "sameAs": [
      "https://www.instagram.com/campervanrentalturkey",
      "https://www.facebook.com/campervanrentalturkey"
    ]
  };

  const cityRoutes: Record<string, string> = {
    'Istanbul': 'campervan-rental-istanbul',
    'Antalya': 'motorhome-hire-antalya',
    'Ankara': 'campervan-rental-ankara',
    'Fethiye': 'motorhome-hire-fethiye',
    'Izmir': 'campervan-rental-izmir',
    'Bodrum': 'campervan-rental-bodrum'
  };

  // URL Parsing and Initial State Setup
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlStartDate = urlParams.get('startDate');
    const urlEndDate = urlParams.get('endDate');
    const urlCity = defaultCity || urlParams.get('city');
    const urlPetsAllowed = urlParams.get('petsAllowed');

    // Redirect legacy city query params to clean URLs
    if (urlCity && !defaultCity && !urlStartDate && !urlEndDate && cityRoutes[urlCity]) {
      const route = cityRoutes[urlCity];
      const newPath = currentLanguage === 'en' ? `/${route}` : `/${currentLanguage}/${route}`;
      navigate(newPath, { replace: true });
      return;
    }

    const parseDate = (dateStr: string | null) => {
      if (!dateStr) return null;
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? null : d;
    };

    const startDate = parseDate(urlStartDate);
    const endDate = parseDate(urlEndDate);

    if (startDate && endDate) {
      const initialFilters: SearchFilters = {
        city: urlCity || 'All',
        startDate,
        endDate,
        passengers: parseInt(urlParams.get('passengers') || '0'),
        petsAllowed: urlParams.get('petsAllowed') === 'true'
      };
      setSearchData(initialFilters);
      setSearchActive(true);
      
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
    } else if (urlCity && urlCity !== 'All') {
      setSearchData({
        city: urlCity,
        startDate: null,
        endDate: null,
        passengers: 0
      });
      setSearchActive(true); // Treat as active search to filter by city
      
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
    } else if (urlPetsAllowed === 'true') {
      setSearchData({
        city: '',
        startDate: null,
        endDate: null,
        passengers: 0,
        petsAllowed: true
      });
      setSearchActive(true);
      
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
    } else {
      const savedSearchData = sessionStorage.getItem('searchData');
      const savedSearchActive = sessionStorage.getItem('searchActive');
      const savedStoredAt = sessionStorage.getItem('searchStoredAt');
      if (savedSearchData && savedSearchActive === 'true' && savedStoredAt) {
        try {
          const storedAt = parseInt(savedStoredAt, 10);
          const now = Date.now();
          if (!isNaN(storedAt) && now - storedAt <= SEARCH_CACHE_TTL) {
            const parsed = JSON.parse(savedSearchData);
            const initialFilters = {
              ...parsed,
              startDate: parsed.startDate ? new Date(parsed.startDate) : undefined,
              endDate: parsed.endDate ? new Date(parsed.endDate) : undefined
            };
            setSearchData(initialFilters);
            setSearchActive(true);
          } else {
            sessionStorage.removeItem('searchData');
            sessionStorage.removeItem('searchActive');
            sessionStorage.removeItem('searchStoredAt');
          }
        } catch (e) {
          sessionStorage.removeItem('searchData');
          sessionStorage.removeItem('searchActive');
          sessionStorage.removeItem('searchStoredAt');
          console.error('Error parsing session search data', e);
        }
      }
    }
  }, [location.search, defaultCity, currentLanguage]);

  const handleSearch = useCallback((filters: SearchFilters) => {
    setSearchActive(true);
    setSearchData(filters);

    // Update URL
    const hasCityRoute = filters.city && filters.city !== 'All' && cityRoutes[filters.city];
    
    if (hasCityRoute) {
      const route = cityRoutes[filters.city];
      const newPath = currentLanguage === 'en' ? `/${route}` : `/${currentLanguage}/${route}`;
      const params = new URLSearchParams();
      if (filters.startDate) params.set('startDate', filters.startDate.toISOString());
      if (filters.endDate) params.set('endDate', filters.endDate.toISOString());
      if (filters.passengers) params.set('passengers', filters.passengers.toString());
      if (filters.petsAllowed) params.set('petsAllowed', 'true');
      const queryString = params.toString();
      navigate(`${newPath}${queryString ? '?' + queryString : ''}`, { replace: true });
    } else {
      const params = new URLSearchParams();
      if (filters.city && filters.city !== 'All') params.set('city', filters.city);
      if (filters.startDate) params.set('startDate', filters.startDate.toISOString());
      if (filters.endDate) params.set('endDate', filters.endDate.toISOString());
      if (filters.passengers) params.set('passengers', filters.passengers.toString());
      if (filters.petsAllowed) params.set('petsAllowed', 'true');
      const basePath = currentLanguage === 'en' ? '/' : `/${currentLanguage}`;
      navigate(`${basePath}?${params.toString()}`, { replace: true });
    }

    sessionStorage.setItem('searchData', JSON.stringify(filters));
    sessionStorage.setItem('searchActive', 'true');
    sessionStorage.setItem('searchStoredAt', Date.now().toString());

    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, [navigate, currentLanguage]);

  const handleSearchChange = (filters: SearchFilters | null, active: boolean) => {
    setSearchData(filters);
    setSearchActive(active);
    if (!active) {
       sessionStorage.removeItem('searchData');
       sessionStorage.removeItem('searchActive');
    }
  };

  return (
    <div className="min-h-screen max-w-full overflow-x-hidden">
      <SchemaJSONLD data={organizationSchema} />
      <Header />
      <main>
        {/* Hero Section */}
        <section id="booking-engine-section" className="px-3 md:px-6 pt-0 pb-6">
          <div className="relative md:rounded-3xl overflow-hidden md:min-h-[600px] flex flex-col justify-center pt-4 pb-2 md:p-12">
            {/* Hero image - desktop only (optimized for mobile prevention) */}
            <picture className="absolute inset-0 w-full h-full">
              <source media="(min-width: 768px)" srcSet="/hero-bg-optimized.webp" type="image/webp" />
              <img
                src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
                alt=""
                className="w-full h-full object-cover md:block hidden"
              />
            </picture>
            <div className="absolute inset-0 bg-black/30 hidden md:block"></div>
            <div className="relative z-10 w-full max-w-6xl mx-auto flex flex-col items-center md:items-start">
              <div className="mb-4 md:mb-8 md:text-left text-center">
                <div className="inline-block bg-red-700 rounded-full md:rounded-lg px-6 py-2 shadow-lg mb-4">
                  <p className="text-sm font-dm-sans font-medium text-white">{t("Only %20 Payment to Book")}</p>
                </div>
                {/* Mobile visible H1 for LCP */}
                <h1 className="md:hidden font-bold text-gray-900 text-center mb-4 text-xl whitespace-pre-line">
                  {HOME_HERO_TITLES[currentLanguage] || HOME_HERO_TITLES.en}
                </h1>
                {/* Desktop H1 */}
                <h1 className="hidden md:block text-4xl lg:text-5xl font-dm-sans font-medium text-white mb-2 leading-tight whitespace-pre-line">
                  {HOME_HERO_TITLES[currentLanguage] || HOME_HERO_TITLES.en}
                </h1>
                <p className="hidden md:block text-xl lg:text-2xl font-dm-sans font-medium text-white mb-8">{t("– Explore Turkey with Freedom and Comfort")}</p>
              </div>
              {!isSearchEngineLoaded ? (
                <SearchEngineFacade 
                  onInteraction={() => setIsSearchEngineLoaded(true)} 
                  id="main-search"
                />
              ) : (
                <Suspense fallback={<SearchEngineFacade onInteraction={() => {}} id="main-search" />}>
                  <SearchEngine key={`search-${currentLanguage}`} id="main-search" onSearch={handleSearch} initialValues={searchData} />
                </Suspense>
              )}
              <div className="mt-3 md:mt-4 md:inline-block md:bg-black/30 md:px-4 md:py-2 md:rounded-lg">
                <p className="text-sm md:text-lg lg:text-xl font-dm-sans font-medium text-foreground md:text-white text-center">
                  {t("Pick up and drop off your campervan in İstanbul, Antalya, Cappadocia and more for ultimate flexibility.")}
                </p>
              </div>
            </div>
          </div>
        </section>

        <LazyLoadSection minHeight="400px" rootMargin="100px">
          <Suspense fallback={<div className="h-[400px] w-full bg-gradient-to-b from-orange-50 to-white animate-pulse" />}>
            <SpecialOffersSection searchParams={searchData || undefined} />
          </Suspense>
        </LazyLoadSection>

        <section id="featured-campervans" ref={resultsRef} className="container mx-auto px-3 sm:px-4 py-12 sm:py-16 max-w-full overflow-x-hidden scroll-mt-20 min-h-[700px]">
          <LazyLoadSection minHeight="600px" rootMargin="200px">
            <Suspense fallback={<div className="h-[600px] w-full bg-muted animate-pulse rounded-xl" />}>
              <CaravanListingSection 
                defaultCity={defaultCity}
                searchData={searchData}
                searchActive={searchActive}
                onSearchChange={handleSearchChange}
              />
            </Suspense>
          </LazyLoadSection>
        </section>

        <LazyLoadSection minHeight="400px" rootMargin="200px">
          <Suspense fallback={<div className="h-40" />}>
            <WideSelectionSection />
          </Suspense>
        </LazyLoadSection>

        <section className="bg-emerald-700 py-4 sm:py-6 overflow-hidden">
          <div className="animate-marquee whitespace-nowrap flex items-center">
            {[1, 2, 3, 4].map((i) => (
              <span key={i} className="text-white text-base sm:text-lg md:text-xl lg:text-2xl font-semibold mx-4 sm:mx-6 md:mx-8">
                {t("ONLY 20% DOWNPAYMENT TO BOOK")}
              </span>
            ))}
          </div>
        </section>

        <LazyLoadSection minHeight="280px" rootMargin="200px">
          <Suspense fallback={<div className="h-40" />}>
            <ChooseExploreSection />
          </Suspense>
        </LazyLoadSection>

        <LazyLoadSection minHeight="200px" rootMargin="200px">
          <Suspense fallback={<div className="h-40" />}>
            <GetToKnowUsSection />
          </Suspense>
        </LazyLoadSection>

        <LazyLoadSection minHeight="400px" rootMargin="200px">
          <Suspense fallback={<div className="h-40" />}>
            <OtherServicesSection />
          </Suspense>
        </LazyLoadSection>

        <LazyLoadSection minHeight="400px" rootMargin="200px">
          <Suspense fallback={<div className="h-40" />}>
            <PopularLocationsSection />
          </Suspense>
        </LazyLoadSection>

        <LazyLoadSection minHeight="300px" rootMargin="200px">
          <Suspense fallback={<div className="h-40" />}>
            <PartnerProgramSection />
          </Suspense>
        </LazyLoadSection>

        <LazyLoadSection minHeight="400px" rootMargin="200px">
          <Suspense fallback={<div className="h-40" />}>
            <BlogSection />
          </Suspense>
        </LazyLoadSection>

        <LazyLoadSection minHeight="300px" rootMargin="200px">
          <Suspense fallback={<div className="h-40" />}>
            <FAQSection />
          </Suspense>
        </LazyLoadSection>
      </main>
      
      <LazyLoadSection minHeight="300px" rootMargin="200px">
        <Suspense fallback={<div className="h-20" />}>
          <Footer />
        </Suspense>
      </LazyLoadSection>
    </div>
  );
};

export default Index;
